import React, { memo } from 'react'
import Button from '@material-ui/core/Button'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import ArrowRightIcon from '@material-ui/icons/ArrowRight'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import Grow from '@material-ui/core/Grow'
import Paper from '@material-ui/core/Paper'
import Popper from '@material-ui/core/Popper'
import MenuItem from '@material-ui/core/MenuItem'
import MenuList from '@material-ui/core/MenuList'
import { makeStyles } from '@material-ui/core'

interface SimulationsButtonProps {
  handleScenarioChange(url: string): void
  options: {
    url: string | undefined
    name: string
    active?: boolean
  }[]
  activeUrl: string
}

const useStyles = makeStyles(() => ({
  root: {
    position: 'absolute',
    top: 10,
    left: 10
  },
  optionsMenu: {
    display: 'flex',
    padding: 0
  },
  option: {
    borderRadius: 0,
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4
  }
}))

const SimulationsButton: React.FC<SimulationsButtonProps> = ({
  handleScenarioChange,
  options,
  activeUrl
}) => {
  const classes = useStyles()
  const [open, setOpen] = React.useState(false)
  const anchorRef = React.useRef<HTMLDivElement>(null)

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    url: string
  ) => {
    handleScenarioChange(url)
    setOpen(false)
  }

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen)
  }

  const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return
    }

    setOpen(false)
  }

  function ensure<T>(
    argument: T | undefined | null,
    message = 'This value was promised to be there.'
  ): T {
    if (argument === undefined || argument === null) {
      throw new TypeError(message)
    }

    return argument
  }

  return (
    <div className={classes.root}>
      <ButtonGroup
        variant="contained"
        color="primary"
        ref={anchorRef}
        aria-label="split button"
      >
        <Button>{ensure(options.find(x => x.url === activeUrl)).name}</Button>
        <Button
          color="primary"
          size="small"
          className="cancel"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          draggable={false}
          onClick={handleToggle}
        >
          <ArrowRightIcon />
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="right"
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'left' ? 'right' : 'center left'
            }}
          >
            <Paper className={classes.option}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  id="split-button-menu"
                  className={classes.optionsMenu}
                >
                  {options.map(option => {
                    if (option.url && option.active) {
                      return (
                        <MenuItem
                          className="cancel"
                          key={option.name}
                          selected={option.url === activeUrl}
                          onClick={event =>
                            handleMenuItemClick(event, option.url || '')
                          }
                        >
                          {option.name}
                        </MenuItem>
                      )
                    } else {
                      return ''
                    }
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  )
}

export default memo(SimulationsButton)
